/**
 * XLS/XLSX sešit → `string[][]`, tedy stejný mezitvar jako z CSV.
 *
 * Díky tomu se zbytek pipeline (detekce hlavičky, mapování, transformace)
 * nemusí o formát vůbec starat.
 */

export type XlsxResult = {
    rows: string[][]
    sheetNames: string[]
    activeSheet: string
}

/**
 * SheetJS je ~400 kB, takže se načítá dynamicky — do hlavního bundlu nepatří,
 * když většina uživatelů nahrává CSV.
 */
export async function readXlsx(buffer: ArrayBuffer, sheetName?: string): Promise<XlsxResult> {
    const XLSX = await import('xlsx')

    // cellDates: bez něj vrací Excel datum jako sériové číslo (45872) a na
    // backend by doputovalo číslo místo data.
    const wb = XLSX.read(buffer, { type: 'array', cellDates: true })
    const sheetNames = wb.SheetNames ?? []

    if (sheetNames.length === 0) {
        return { rows: [], sheetNames: [], activeSheet: '' }
    }

    const active = sheetName && sheetNames.includes(sheetName) ? sheetName : sheetNames[0]
    const sheet = wb.Sheets[active]

    // `raw: false` dává hezký text pro čísla, ale datum by vrátilo podle
    // formátu buňky (např. „8/3/26“) — tedy bez času a s nejednoznačným
    // pořadím dne a měsíce. Datumové buňky proto bereme jako Date z `raw: true`
    // a zbytek z formátované varianty.
    const opts = { header: 1 as const, defval: '', blankrows: false }
    const formatted = XLSX.utils.sheet_to_json<unknown[]>(sheet, { ...opts, raw: false })
    const native = XLSX.utils.sheet_to_json<unknown[]>(sheet, { ...opts, raw: true })

    const rows = formatted.map((row, r) =>
        (row ?? []).map((cell, c) => {
            const nativeCell = native[r]?.[c]
            return toCell(nativeCell instanceof Date ? nativeCell : cell)
        })
    )

    return { rows: normaliseWidth(rows), sheetNames, activeSheet: active }
}

/**
 * Buňka → text. `Date` se serializuje do `YYYY-MM-DD HH:MM`, což je jeden
 * z formátů, které backend přijímá.
 */
function toCell(value: unknown): string {
    if (value === null || value === undefined) return ''
    if (value instanceof Date) return formatDate(value)
    return String(value).trim()
}

function formatDate(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

function normaliseWidth(rows: string[][]): string[][] {
    const width = rows.reduce((max, r) => Math.max(max, r.length), 0)
    return rows.map(r => (r.length === width ? r : [...r, ...Array(width - r.length).fill('')]))
}
